import type { ICellRendererComp, ICellRendererParams, GridApi } from "ag-grid-enterprise";
import { showConfirmModal } from "../confirm_modal";

interface DropdownActionsCellRendererParams extends ICellRendererParams {
  // Opt-in — default false keeps existing consumers' viewport-collision flip-to-above
  // behavior; Admin Users passes true for legacy's plain below-only positioning.
  alwaysBelow?: boolean;
  // Opt-in — default false opens leftward (tuned for a right-pinned column); Admin
  // Users' left-pinned trigger passes true to open rightward instead.
  openRightward?: boolean;
}

// Duplicated from ActionsCellRenderer's row_actions shape rather than shared, so this
// renderer stays a fully independent sibling with no regression risk to that one.
interface RowAction {
  type?: "badge" | "action";
  label: string;
  url?: string;
  icon?: string;
  method?: string;
  remote?: boolean;
  rel?: string;
  target?: string;
  confirm?: string;
  confirm_title?: string;
  confirm_danger?: string;
  turbo_frame?: string;
}

// Only reloads on a genuine 2xx response — a blind reload would mask a failed
// action (wrong status, network error) as if it had succeeded.
async function executeAction(action: RowAction): Promise<void> {
  const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
  try {
    const response = await fetch(action.url ?? "#", {
      method: (action.method ?? "get").toUpperCase(),
      headers: {
        "X-CSRF-Token": csrf,
        "X-Requested-With": "XMLHttpRequest",
        "Accept": "text/html, application/xhtml+xml",
      },
      credentials: "same-origin",
    });
    if (!response.ok) throw new Error(`Request failed (${response.status})`);
    window.location.reload();
  } catch {
    alert(`Failed to complete "${action.label}". Please try again.`);
  }
}

// Menu is portalled to `document.body` (not appended inside `eGui`), so it escapes
// the grid row's fixed height / `overflow: hidden` clipping instead of growing the row.
export class DropdownActionsCellRenderer implements ICellRendererComp {
  private eGui!: HTMLElement;
  private button!: HTMLButtonElement;
  private menu: HTMLElement | null = null;
  private menuItemEls: HTMLElement[] = [];
  private actions: RowAction[] = [];
  private activeIndex = 0;
  private gridApi: GridApi | null = null;
  private alwaysBelow = false;
  private openRightward = false;

  init(params: DropdownActionsCellRendererParams): void {
    this.eGui = document.createElement("div");
    this.eGui.className = "ag-dropdown-actions-cell";

    this.alwaysBelow = params.alwaysBelow ?? false;
    this.openRightward = params.openRightward ?? false;

    if (params.node.group) return;

    const rowActions = (params.data as Record<string, unknown>)?.row_actions;
    if (!Array.isArray(rowActions) || rowActions.length === 0) return;

    this.gridApi = params.api;

    // Badges keep ActionsCellRenderer's inline presentation (shared class name for
    // visual parity) — only non-badge actions move into the dropdown.
    (rowActions as RowAction[]).forEach((action) => {
      if (action.type === "badge") {
        const badge = document.createElement("span");
        badge.className = "ag-actions-cell__badge";
        badge.textContent = action.label;
        this.eGui.appendChild(badge);
      }
    });

    this.actions = (rowActions as RowAction[]).filter((action) => action.type !== "badge");
    if (this.actions.length === 0) return;

    this.buildTrigger();
    this.eGui.appendChild(this.button);
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  refresh(): boolean {
    return false;
  }

  /** Must tear down every document/grid-api listener and remove any open menu,
   * or listeners leak across every row ever rendered. */
  destroy(): void {
    this.closeMenu(false);
  }

  // ─── Trigger button ───────────────────────────────────────────────────────

  private buildTrigger(): void {
    this.button = document.createElement("button");
    this.button.type = "button";
    this.button.className = "toolkit-button-component toolkit-button-component--toolbar-chip ag-dropdown-actions-cell__trigger";
    this.button.setAttribute("aria-haspopup", "menu");
    this.button.setAttribute("aria-expanded", "false");
    this.button.setAttribute("aria-label", "Actions");

    const icon = document.createElement("i");
    icon.className = "fas fa-ellipsis-h";
    icon.setAttribute("aria-hidden", "true");
    this.button.appendChild(icon);

    this.button.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.menu) {
        this.closeMenu(false);
      } else {
        this.openMenu();
      }
    });

    // Let the trigger open the menu with the keyboard without also handing the
    // keystroke to AG-Grid's own cell/row navigation.
    this.button.addEventListener("keydown", (e) => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        if (!this.menu) this.openMenu();
        this.focusItem(e.key === "ArrowUp" ? this.menuItemEls.length - 1 : 0);
      }
    });
  }

  // ─── Menu open/close ──────────────────────────────────────────────────────

  private isOpen(): boolean {
    return this.menu !== null;
  }

  /** Public wrapper so callers outside this class (e.g. AgGridSsrmComponent's
   * checkboxOpensActionsMenu handler) can open this row's menu programmatically —
   * no-ops if there are no actions to show (mirrors init()'s own guard). */
  openMenu(): void {
    if (this.actions.length === 0) return;
    this.openMenuInternal();
  }

  private openMenuInternal(): void {
    if (this.menu) return;

    this.menu = document.createElement("div");
    this.menu.className = "ag-dropdown-actions-menu";
    this.menu.setAttribute("role", "menu");

    this.menuItemEls = this.actions.map((action, index) => this.buildMenuItem(action, index));
    this.menuItemEls.forEach((el) => this.menu?.appendChild(el));

    document.body.appendChild(this.menu);
    this.positionMenu();

    this.button.setAttribute("aria-expanded", "true");
    this.activeIndex = 0;
    this.focusItem(0);

    document.addEventListener("click", this.onDocumentClick, true);
    document.addEventListener("keydown", this.onDocumentKeyDown, true);
    // capture: true — grid body scroll happens on an inner scrollable div, which
    // doesn't bubble a "scroll" event up to window/document.
    document.addEventListener("scroll", this.onCloseTrigger, true);
    window.addEventListener("resize", this.onCloseTrigger);
    this.gridApi?.addEventListener("bodyScroll", this.onCloseTrigger);
    this.gridApi?.addEventListener("columnResized", this.onCloseTrigger);
    this.gridApi?.addEventListener("columnMoved", this.onCloseTrigger);
  }

  private closeMenu(returnFocus: boolean): void {
    if (!this.menu) return;

    this.menu.remove();
    this.menu = null;
    this.menuItemEls = [];
    this.button.setAttribute("aria-expanded", "false");

    document.removeEventListener("click", this.onDocumentClick, true);
    document.removeEventListener("keydown", this.onDocumentKeyDown, true);
    document.removeEventListener("scroll", this.onCloseTrigger, true);
    window.removeEventListener("resize", this.onCloseTrigger);
    this.gridApi?.removeEventListener("bodyScroll", this.onCloseTrigger);
    this.gridApi?.removeEventListener("columnResized", this.onCloseTrigger);
    this.gridApi?.removeEventListener("columnMoved", this.onCloseTrigger);

    if (returnFocus) this.button.focus();
  }

  // Bound instance methods (not inline closures) so add/removeEventListener reference
  // the same function identity.
  private onCloseTrigger = (): void => {
    this.closeMenu(false);
  };

  private onDocumentClick = (e: MouseEvent): void => {
    const target = e.target as Node;
    if (this.menu?.contains(target) || this.eGui.contains(target)) return;
    this.closeMenu(false);
  };

  private onDocumentKeyDown = (e: KeyboardEvent): void => {
    if (!this.menu) return;

    switch (e.key) {
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        this.closeMenu(true);
        break;
      case "ArrowDown":
        e.preventDefault();
        e.stopPropagation();
        this.focusItem((this.activeIndex + 1) % this.menuItemEls.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        e.stopPropagation();
        this.focusItem((this.activeIndex - 1 + this.menuItemEls.length) % this.menuItemEls.length);
        break;
      case "Home":
        e.preventDefault();
        this.focusItem(0);
        break;
      case "End":
        e.preventDefault();
        this.focusItem(this.menuItemEls.length - 1);
        break;
      case "Tab":
        // Menu items use a roving tabindex, not native tab order — Tab exits the
        // menu entirely, so close rather than leave an orphaned open menu behind.
        this.closeMenu(false);
        break;
      default:
        break;
    }
  };

  private focusItem(index: number): void {
    if (this.menuItemEls.length === 0) return;
    this.activeIndex = index;
    this.menuItemEls.forEach((el, i) => { el.tabIndex = i === index ? 0 : -1; });
    this.menuItemEls[index].focus();
  }

  // ─── Positioning ──────────────────────────────────────────────────────────

  private positionMenu(): void {
    if (!this.menu) return;

    const triggerRect = this.button.getBoundingClientRect();
    const menuRect = this.menu.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let top = triggerRect.bottom + 4;
    if (!this.alwaysBelow && top + menuRect.height > viewportHeight) {
      top = Math.max(4, triggerRect.top - menuRect.height - 4);
    }

    // Default opens leftward (right-pinned tuning); openRightward flips the
    // preferred anchor so a left-pinned trigger (Admin Users) opens into the grid.
    let left: number;
    if (this.openRightward) {
      left = triggerRect.left;
      if (left + menuRect.width > viewportWidth - 4) left = triggerRect.right - menuRect.width;
      if (left < 4) left = 4;
    } else {
      left = triggerRect.right - menuRect.width;
      if (left < 4) left = triggerRect.left;
      if (left + menuRect.width > viewportWidth) left = viewportWidth - menuRect.width - 4;
    }

    this.menu.style.position = "fixed";
    this.menu.style.top = `${top}px`;
    this.menu.style.left = `${left}px`;
  }

  // ─── Menu items (same branching ActionsCellRenderer uses per action) ──────

  private buildMenuItem(action: RowAction, index: number): HTMLElement {
    const item = document.createElement("a");
    item.className = "ag-dropdown-actions-menu__item";
    item.setAttribute("role", "menuitem");
    item.tabIndex = index === 0 ? 0 : -1;
    item.href = action.url || "#";

    if (action.rel) {
      item.rel = action.rel;
    }

    // Turbo Drive skips links with a target. Without one it intercepts the click, gets back the
    // `text/csv` a download redirects to, cannot render it, and the click silently does nothing.
    if (action.target) {
      item.target = action.target;
    }

    // Close on activation, before the action's own side effect runs. `rel` actions defer by a
    // task: jquery-modal's handler is delegated on `document`, so detaching the anchor
    // mid-propagation would stop the event ever reaching it.
    item.addEventListener("click", () => {
      if (action.rel) {
        setTimeout(() => this.closeMenu(false), 0);
      } else {
        this.closeMenu(false);
      }
    });

    if (action.turbo_frame) {
      const frameId = action.turbo_frame;
      const href = action.url || "#";
      item.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const frame = document.querySelector<HTMLElement>(`turbo-frame#${frameId}`);
        if (frame) frame.setAttribute("src", href);
      });
    } else if (action.confirm || action.confirm_title || action.confirm_danger) {
      item.addEventListener("click", async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const confirmed = await showConfirmModal({
          title: action.confirm_title,
          danger: action.confirm_danger,
          message: action.confirm,
          confirmLabel: action.label,
        });
        if (confirmed) await executeAction(action);
      });
    } else if (action.method && action.method !== "get") {
      item.dataset.method = action.method;
      // Same opt-out AgGridBaseController's selection menu honours: an endpoint that answers with
      // a redirect has to navigate, or an XHR follows that redirect invisibly and nothing happens.
      if (action.remote !== false) item.dataset.remote = "true";
    }

    if (action.icon) {
      const icon = document.createElement("i");
      icon.className = `fas fa-${action.icon}`;
      icon.setAttribute("aria-hidden", "true");
      item.appendChild(icon);
    }

    const label = document.createElement("span");
    label.textContent = action.label;
    item.appendChild(label);

    return item;
  }
}
